import { useMemo } from 'react'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { assetUrl } from 'utils/url'
import {
  useAPIContentKinerjaUtama,
  useAPIContentPerilakuKerja,
} from 'api/kinerja/useAPIKinerja'
import { TugasTambahanItems } from 'app/KinerjaGuruPage/components/perencanaan/Detail/TugasTambahanItems'
import { TugasPokokItems } from 'app/KinerjaGuruPage/components/perencanaan/Detail/TugasPokokItems'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { isSelectableTugasPokok } from 'app/KinerjaGuruPage/utils/perencanaan'
import { PraktikKinerjaItem } from 'app/KinerjaGuruPage/components/perencanaan/Detail/PraktikKinerjaItem'
import { PengembanganKompetensiItem } from 'app/KinerjaGuruPage/components/perencanaan/Detail/PengembanganKompetensiItem'
import { PerilakuKerjaItems } from 'app/KinerjaGuruPage/components/perencanaan/Detail/PerilakuKerjaItems'

import type { PerencanaanGuruDetailData } from 'app/KinerjaGuruPage/types/perencanaan'
import type { MultiPeriodPayload } from 'types/kinerja'

export type DetailProps = {
  isLoading: boolean
  periodParams?: MultiPeriodPayload
  data: PerencanaanGuruDetailData
  showReview?: boolean
}

export function Detail({
  isLoading,
  periodParams,
  data,
  showReview,
}: DetailProps) {
  const { periodYear, periodNo } = usePeriodContext()

  // -----------------------------------------
  // Get content data for mapping
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'SPF',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const {
    data: dataContentPerilakuKerja,
    isLoading: isLoadingContentPerilakuKerja,
  } = useAPIContentPerilakuKerja(
    {
      tipe: 'SPF',
      periodParams: periodParams
        ? {
            // Use period params if provided
            skp_year: periodParams.skpYear,
            skp_period: periodParams.skpPeriod,
          }
        : {
            // Use current period as fallback
            skp_year: periodYear,
            skp_period: periodNo,
          },
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const mandatoryTugasPokokLength = useMemo(() => {
    const items =
      dataContentKinerjaUtama?.data?.kinerjaUtama?.filter(
        (item) => !isSelectableTugasPokok(item.tag)
      ) || []
    return items.length
  }, [dataContentKinerjaUtama])

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingAll =
    isLoading || isLoadingContentKinerjaUtama || isLoadingContentPerilakuKerja

  if (isLoadingAll) {
    return (
      <div className="space-y-9">
        <Shimmer height={320} width="100%" className="rounded" />
        <Shimmer height={128} width="100%" className="rounded" />
        <Shimmer height={128} width="100%" className="rounded" />
        <Shimmer height={440} width="100%" className="rounded" />
      </div>
    )
  }

  return (
    <div className="space-y-9">
      {/* Pelaksanaan Tugas Pokok */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={52}
            height={52}
          />
          <Text variant="heading-sm" as="h3">
            Pelaksanaan Tugas Pokok
          </Text>
        </div>
        <div className="space-y-4">
          {/* Selectable Tugas Pokok */}
          <TugasPokokItems
            originalIds={data.tugasPokokIds}
            reviewIds={data.reviewTugasPokokIds}
            showReview={showReview}
            contents={dataContentKinerjaUtama?.data?.kinerjaUtama || []}
            orderNumber={1}
          />

          {/* Tugas Tambahan */}
          <TugasTambahanItems
            originalIds={data.tugasTambahanIds}
            reviewIds={data.reviewTugasTambahanIds}
            showReview={showReview}
            contents={dataContentKinerjaUtama?.data?.tugasTambahan || []}
            orderNumber={mandatoryTugasPokokLength + 2}
          />
        </div>
      </section>

      {/* Praktik Kinerja */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={assetUrl('/illustrations/img-badge-star.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={52}
            height={52}
          />
          <Text variant="heading-sm">Praktik Kinerja</Text>
        </div>
        <PraktikKinerjaItem
          originalId={data.praktikKinerjaId}
          reviewId={data.reviewPraktikKinerjaId}
          showReview={showReview}
          contents={dataContentKinerjaUtama?.data?.praktikPembelajaran || []}
          orderNumber={1}
        />
      </section>

      {/* Pengembangan Kompetensi */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={assetUrl('/illustrations/img-document.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={52}
            height={52}
          />
          <Text variant="heading-sm">Pengembangan Kompetensi</Text>
        </div>
        <PengembanganKompetensiItem
          originalId={data.pengembanganKompetensiId}
          reviewId={data.reviewPengembanganKompetensiId}
          showReview={showReview}
          contents={dataContentKinerjaUtama?.data?.pengembanganKompetensi || []}
          orderNumber={1}
        />
      </section>

      {/* Perilaku Kerja */}
      <section>
        <Text variant="heading-sm" className="mb-4">
          Perilaku Kerja
        </Text>

        <PerilakuKerjaItems
          originalItems={data.perilakuKerjaItems}
          reviewItems={data.reviewPerilakuKerjaItems}
          showReview={showReview}
          contents={dataContentPerilakuKerja?.data?.aspek || []}
        />
      </section>
    </div>
  )
}
